import {
  mysqlTable,
  int,
  bigint,
  varchar,
  text,
  mediumtext,
  longtext,
  double,
  date,
  datetime,
  boolean,
  timestamp,
  mysqlEnum,
  type AnyMySqlColumn,
} from "drizzle-orm/mysql-core";
import { companies, companyAddresses } from "./tenancy";
import { users } from "./identity";
import { teams } from "./hr";
import { currencies } from "./finance";

/**
 * Recruit (ATS) module — Drizzle port.
 *
 * Ported by reconciling each table's Schema::create + EVERY up() ALTER across the
 * Recruit module migrations (IGNORING down()). Anchor file:
 *   Modules/Recruit/Database/Migrations/2021_11_30_070320_create_recruit_skills.php
 *   (630-line bulk create — creates ~28 tables).
 * ALTER reconciliation files:
 *   2022_07_18_082515 (job_applications cols), 2022_09_01_000000 (global settings),
 *   2022_09_15_061628 (job categories + the big FK-rename pass), 2022_10_01_000000
 *   (company_id sweep), 2023_01_10_100953 (custom questions), 2023_01_23 / 2023_02_02
 *   (settings cols), 2023_02_03_121345 (salary structures + more cols), 2023_04_25
 *   (interview_comments.candidate_comment), 2023_11_02 / 2023_12_19 (global settings),
 *   2024_01_12 (FK re-point), 2024_02_16 (application_sources company_id), 2024_03_19
 *   (drop unique), 2024_05_29 (job_histories status_id), 2024_07_16 (jobs ctc-require),
 *   2024_08_20 (offer_letter.description), 2024_09_17 (applications.send_email),
 *   2024_10_23 (FK re-point).
 *
 * KEY PHP-VS-EXPECTATION FLAGS (PHP wins):
 *  - MASS COLUMN RENAMES via raw `DB::statement ... CHANGE`: most FK columns were renamed
 *    from short names to fully-qualified ones in 2022_09_15. Final columns are ported
 *    (e.g. recruit_jobs.job_type_id → recruit_job_type_id; recruit_job_applications.
 *    status_id → recruit_application_status_id, source_id → application_source_id,
 *    file_id → recruit_job_file_id, job_id → recruit_job_id; jobboard board_column_id →
 *    recruit_application_status_id; interview_schedules stage_id → recruit_interview_stage_id;
 *    offer_letter job_app_id → recruit_job_application_id, job_id → recruit_job_id;
 *    job_addresses job_id/address_id → recruit_job_id/company_address_id; etc.).
 *  - recruit_jobs: `job_type` enum (part time/full time) coexists with the `recruit_job_type_id`
 *    FK column — BOTH exist (the enum was the original create line 58; the FK col is line 46
 *    renamed). `location_id` was DROPPED (2023_02_03_121345 lines 116-127) — NOT ported.
 *    `currency_id` added (FK → currencies, deferred) with onDelete SET NULL (2023_02_03 line 19).
 *  - recruit_jobs.recruit_job_type_id / recruit_work_experience_id are BIGINT UNSIGNED after the
 *    CHANGE statements (2022_09_15 lines 94/98), even though created as the same width.
 *  - recruit_job_applications.current_ctc: created string → CHANGE to INT(11) (2022_07_18 line 24)
 *    → CHANGE to DOUBLE NULL (2022_09_15 line 53). Final = double nullable. expected_ctc: created
 *    string → DOUBLE NULL (2022_09_15 line 55). total_experience enum widened twice (final list
 *    from 2023_02_03 line 135). application_sources enum stays (careerWebsite/addedByUser).
 *  - recruit_settings: created with `purchase_code`, which is then DROPPED in 2022_09_01 (moved to
 *    recruit_global_settings) — NOT ported. Cols added across 5 migrations (form_settings,
 *    application_restriction, career_site, offer_letter_reminder, job_alert_status,
 *    google_recaptcha_status).
 *  - recruit_job_offer_letter.comp_amount: created double → CHANGE to DOUBLE NULL (2023_02_03
 *    line 34). employee_id gets FK → users SET NULL added via raw SQL (2022_07_18 line 37).
 *    +add_structure (int default 0), +description (longText).
 *  - application_sources: had a UNIQUE on application_source originally (dropped 2024_03_19,
 *    try/catch-guarded). NOT modelled as unique in final state. +company_id +is_predefined.
 *  - recruit_job_histories.recruit_job_application_status_id added LATE (2024_05_29) — separate
 *    from the 2022_09_15 rename pass.
 *  - recruit_interview_evaluations.submitted_by: `unsignedInteger(...)->unsigned()` (double-call,
 *    harmless) FK → users with onDelete(null) — ported as a plain FK without onDelete.
 *
 * Deferred cross-batch FKs (column kept, NO .references()):
 *   recruit_jobs.department_id → teams; recruit_jobs.currency_id → currencies;
 *   recruit_job_alerts.recruit_job_category_id → recruit_job_categories (its create file lists
 *     the FK but that table is created in a LATER-named migration that actually runs first by
 *     guard — kept as own-file ref, see below); recruit_selected_salary_components.salary_component_id
 *     → salary_components; recruit_job_offer_letter.employee_id → users (raw-SQL FK, kept as ref).
 *   recruit_candidate_database has NO FKs declared (job_id/location_id/job_application_id are bare
 *     unsigned bigints; job_id renamed to recruit_job_id in 2022_09_15 line 343).
 */

/* ============================== LOOKUPS / TAXONOMY ============================== */

/** recruit_skills — int PK. +company_id (2022_10_01). */
export const recruitSkills = mysqlTable("recruit_skills", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  name: varchar("name", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_job_types — bigint id ($table->id()). +company_id (2022_10_01). */
export const recruitJobTypes = mysqlTable("recruit_job_types", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  jobType: varchar("job_type", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_work_experiences — bigint id. +company_id (2022_10_01). */
export const recruitWorkExperiences = mysqlTable("recruit_work_experiences", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  workExperience: varchar("work_experience", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_interview_stages — int PK. +company_id (2022_10_01). */
export const recruitInterviewStages = mysqlTable("recruit_interview_stages", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  name: varchar("name", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_application_status_categories — bigint id. +company_id (2022_10_01). */
export const recruitApplicationStatusCategories = mysqlTable("recruit_application_status_categories", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  name: varchar("name", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * application_sources — int PK. +company_id + is_predefined (2022_10_01 / 2024_02_16).
 * Original UNIQUE on application_source dropped (2024_03_19), so no unique here.
 */
export const applicationSources = mysqlTable("application_sources", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  applicationSource: varchar("application_source", { length: 255 }).notNull(),
  isPredefined: boolean("is_predefined").default(true),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_job_categories — int PK. company_id. (created 2022_09_15) */
export const recruitJobCategories = mysqlTable("recruit_job_categories", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  categoryName: text("category_name").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_job_sub_categories — int PK. company_id + recruit_job_category_id. (created 2022_09_15) */
export const recruitJobSubCategories = mysqlTable("recruit_job_sub_categories", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  recruitJobCategoryId: int("recruit_job_category_id", { unsigned: true })
    .notNull()
    .references(() => recruitJobCategories.id, { onDelete: "cascade", onUpdate: "cascade" }),
  subCategoryName: text("sub_category_name").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_recommendation_statuses — int PK. +company_id (2022_10_01). */
export const recruitRecommendationStatuses = mysqlTable("recruit_recommendation_statuses", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  status: varchar("status", { length: 255 }).notNull(),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ============================== JOBS ============================== */

/**
 * recruit_jobs — bigint id, softDeletes.
 * Renames: job_type_id → recruit_job_type_id (bigint unsigned nullable),
 *   work_experience_id → recruit_work_experience_id (bigint unsigned nullable).
 * Dropped: location_id (2023_02_03).
 * Added: remote_job, disclose_salary, recruit_job_category_id, recruit_job_sub_category_id,
 *   currency_id (→ currencies, set null/cascade), is_currentctc_require, is_expectedctc_require.
 * department_id → teams (cascade/cascade). end_date became nullable (2022_07_18 line 36).
 */
export const recruitJobs = mysqlTable("recruit_jobs", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  title: text("title").notNull(),
  slug: varchar("slug", { length: 255 }),
  recruitJobTypeId: bigint("recruit_job_type_id", { mode: "number", unsigned: true }).references(
    () => recruitJobTypes.id,
    { onDelete: "set null", onUpdate: "cascade" },
  ),
  jobDescription: longtext("job_description"),
  totalPositions: int("total_positions").notNull(),
  remainingOpenings: int("remaining_openings").notNull(),
  departmentId: int("department_id", { unsigned: true }).references(() => teams.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  recruiterId: int("recruiter_id").notNull(),
  jobType: mysqlEnum("job_type", ["part time", "full time"]).default("full time"),
  startDate: datetime("start_date").notNull(),
  endDate: datetime("end_date"),
  status: mysqlEnum("status", ["open", "closed"]).default("open"),
  remoteJob: mysqlEnum("remote_job", ["yes", "no"]).default("no"),
  discloseSalary: mysqlEnum("disclose_salary", ["yes", "no"]).default("no"),
  currencyId: int("currency_id", { unsigned: true }).references(() => currencies.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  recruitJobCategoryId: int("recruit_job_category_id", { unsigned: true }).references(
    () => recruitJobCategories.id,
    { onDelete: "set null", onUpdate: "cascade" },
  ),
  recruitJobSubCategoryId: int("recruit_job_sub_category_id", { unsigned: true }).references(
    () => recruitJobSubCategories.id,
    { onDelete: "set null", onUpdate: "cascade" },
  ),
  metaDetails: text("meta_details").notNull(),
  isPhotoRequire: boolean("is_photo_require").default(false),
  isResumeRequire: boolean("is_resume_require").default(false),
  isDobRequire: boolean("is_dob_require").default(false),
  isGenderRequire: boolean("is_gender_require").default(false),
  recruitWorkExperienceId: bigint("recruit_work_experience_id", { mode: "number", unsigned: true }).references(
    () => recruitWorkExperiences.id,
    { onDelete: "set null", onUpdate: "cascade" },
  ),
  payType: varchar("pay_type", { length: 255 }).notNull(),
  startAmount: double("start_amount").notNull(),
  endAmount: double("end_amount"),
  payAccording: mysqlEnum("pay_according", ["hour", "day", "week", "month", "year"]).notNull(),
  isCurrentctcRequire: boolean("is_currentctc_require").default(false),
  isExpectedctcRequire: boolean("is_expectedctc_require").default(false),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
  deletedAt: timestamp("deleted_at"),
});

/** recruit_job_skills — int PK. job_id → recruit_job_id (bigint), skill_id → recruit_skill_id. */
export const recruitJobSkills = mysqlTable("recruit_job_skills", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  recruitJobId: bigint("recruit_job_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => recruitJobs.id, { onDelete: "cascade", onUpdate: "cascade" }),
  recruitSkillId: int("recruit_skill_id", { unsigned: true })
    .notNull()
    .references(() => recruitSkills.id, { onDelete: "cascade", onUpdate: "cascade" }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_job_addresses — composite (no PK). job_id → recruit_job_id, address_id → company_address_id. */
export const recruitJobAddresses = mysqlTable("recruit_job_addresses", {
  recruitJobId: bigint("recruit_job_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => recruitJobs.id, { onDelete: "cascade", onUpdate: "cascade" }),
  companyAddressId: bigint("company_address_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => companyAddresses.id, { onDelete: "cascade", onUpdate: "cascade" }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_job_files — int PK. job_id → recruit_job_id (bigint). */
export const recruitJobFiles = mysqlTable("recruit_job_files", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  recruitJobId: bigint("recruit_job_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => recruitJobs.id, { onDelete: "cascade", onUpdate: "cascade" }),
  filename: varchar("filename", { length: 255 }).notNull(),
  description: text("description"),
  googleUrl: varchar("google_url", { length: 255 }),
  hashname: varchar("hashname", { length: 255 }),
  size: varchar("size", { length: 255 }),
  dropboxLink: varchar("dropbox_link", { length: 255 }),
  externalLink: varchar("external_link", { length: 255 }),
  externalLinkName: varchar("external_link_name", { length: 255 }),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ============================== APPLICATION STATUS ============================== */

/**
 * recruit_application_status — int PK. category_id → recruit_application_status_category_id (bigint).
 * +company_id (2022_10_01).
 */
export const recruitApplicationStatus = mysqlTable("recruit_application_status", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  status: varchar("status", { length: 255 }).notNull(),
  slug: varchar("slug", { length: 255 }).notNull(),
  color: varchar("color", { length: 255 }).notNull(),
  position: int("position").notNull(),
  recruitApplicationStatusCategoryId: bigint("recruit_application_status_category_id", {
    mode: "number",
    unsigned: true,
  })
    
    .references(() => recruitApplicationStatusCategories.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
  action: mysqlEnum("action", ["yes", "no"]).default("no"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ============================== APPLICATIONS ============================== */

/**
 * recruit_job_applications — int PK, softDeletes.
 * Renames: job_id → recruit_job_id (bigint), status_id → recruit_application_status_id,
 *   source_id → application_source_id, file_id → recruit_job_file_id.
 * Added cols: total_experience (enum, widened), current_location, current_ctc (→double),
 *   expected_ctc (→double), notice_period (enum), currenct_ctc_rate, expected_ctc_rate,
 *   rejection_remark, send_email. application_sources enum (careerWebsite/addedByUser).
 */
export const recruitJobApplications = mysqlTable("recruit_job_applications", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  fullName: varchar("full_name", { length: 255 }).notNull(),
  email: varchar("email", { length: 191 }),
  phone: varchar("phone", { length: 191 }),
  dateOfBirth: date("date_of_birth"),
  gender: varchar("gender", { length: 255 }),
  totalExperience: mysqlEnum("total_experience", [
    "fresher",
    "0-1",
    "1-2",
    "2-3",
    "3-4",
    "4-5",
    "5-6",
    "6-7",
    "7-8",
    "8-9",
    "9-10",
    "10-11",
    "11-12",
    "12-13",
    "13-14",
    "over-15",
  ]),
  currentLocation: varchar("current_location", { length: 255 }),
  currentCtc: double("current_ctc"),
  currenctCtcRate: varchar("currenct_ctc_rate", { length: 255 }),
  expectedCtc: double("expected_ctc"),
  expectedCtcRate: varchar("expected_ctc_rate", { length: 255 }),
  noticePeriod: mysqlEnum("notice_period", ["15", "30", "45", "60", "75", "90", "over-90"]),
  photo: varchar("photo", { length: 255 }),
  resume: text("resume").notNull(),
  columnPriority: int("column_priority"),
  remark: varchar("remark", { length: 255 }).notNull(),
  rejectionRemark: varchar("rejection_remark", { length: 255 }),
  coverLetter: mediumtext("cover_letter"),
  jobType: mysqlEnum("job_type", ["part time", "full time", "internship"]).default("full time"),
  recruitJobId: bigint("recruit_job_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => recruitJobs.id, { onDelete: "cascade", onUpdate: "cascade" }),
  recruitApplicationStatusId: int("recruit_application_status_id", { unsigned: true })
    
    .references(() => recruitApplicationStatus.id, { onDelete: "cascade", onUpdate: "cascade" }),
  locationId: bigint("location_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => companyAddresses.id, { onDelete: "cascade", onUpdate: "cascade" }),
  applicationSources: mysqlEnum("application_sources", ["careerWebsite", "addedByUser"]).notNull(),
  applicationSourceId: int("application_source_id", { unsigned: true }).references(
    () => applicationSources.id,
    { onDelete: "set null", onUpdate: "cascade" },
  ),
  recruitJobFileId: int("recruit_job_file_id", { unsigned: true }).references(
    () => recruitJobFiles.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  sendEmail: boolean("send_email"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
  deletedAt: timestamp("deleted_at"),
});

/** recruit_applicant_notes — int PK. job_application_id → recruit_job_application_id. */
export const recruitApplicantNotes = mysqlTable("recruit_applicant_notes", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  recruitJobApplicationId: int("recruit_job_application_id", { unsigned: true })
    .notNull()
    .references(() => recruitJobApplications.id, { onDelete: "cascade", onUpdate: "cascade" }),
  noteText: text("note_text").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_application_skills — int PK. application_id → recruit_job_application_id, skill_id → recruit_skill_id. */
export const recruitApplicationSkills = mysqlTable("recruit_application_skills", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  recruitJobApplicationId: int("recruit_job_application_id", { unsigned: true })
    .notNull()
    .references(() => recruitJobApplications.id, { onDelete: "cascade", onUpdate: "cascade" }),
  recruitSkillId: int("recruit_skill_id", { unsigned: true })
    .notNull()
    .references(() => recruitSkills.id, { onDelete: "cascade", onUpdate: "cascade" }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_application_files — int PK. application_id → recruit_job_application_id. */
export const recruitApplicationFiles = mysqlTable("recruit_application_files", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  recruitJobApplicationId: int("recruit_job_application_id", { unsigned: true })
    .notNull()
    .references(() => recruitJobApplications.id, { onDelete: "cascade", onUpdate: "cascade" }),
  filename: varchar("filename", { length: 255 }).notNull(),
  hashname: varchar("hashname", { length: 255 }).notNull(),
  size: varchar("size", { length: 255 }).notNull(),
  description: text("description"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * recruit_candidate_database — int PK. job_id → recruit_job_id (bigint). NO FKs declared in PHP.
 * location_id / job_application_id are bare unsigned bigints (no .references()).
 */
export const recruitCandidateDatabase = mysqlTable("recruit_candidate_database", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  name: varchar("name", { length: 255 }).notNull(),
  recruitJobId: bigint("recruit_job_id", { mode: "number", unsigned: true }).notNull(), // renamed from job_id; no FK in PHP
  locationId: bigint("location_id", { mode: "number", unsigned: true }).notNull(), // no FK in PHP
  jobAppliedOn: date("Job_applied_on").notNull(),
  skills: longtext("skills").notNull(),
  jobApplicationId: bigint("job_application_id", { mode: "number", unsigned: true }).notNull(), // no FK in PHP
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * recruit_candidate_follow_ups — int PK. (created 2023_02_03)
 */
export const recruitCandidateFollowUps = mysqlTable("recruit_candidate_follow_ups", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  recruitJobApplicationId: int("recruit_job_application_id", { unsigned: true }).references(
    () => recruitJobApplications.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  remark: longtext("remark"),
  nextFollowUpDate: datetime("next_follow_up_date"),
  sendReminder: mysqlEnum("send_reminder", ["yes", "no"]).default("no"),
  remindTime: text("remind_time"),
  remindType: mysqlEnum("remind_type", ["minute", "hour", "day"]),
  status: varchar("status", { length: 255 }).default("incomplete"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ============================== INTERVIEWS ============================== */

/**
 * recruit_interview_schedules — int PK. job_application_id → recruit_job_application_id,
 * stage_id → recruit_interview_stage_id. parent_id self-ref. +company_id (2022_10_01).
 */
export const recruitInterviewSchedules = mysqlTable("recruit_interview_schedules", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  recruitJobApplicationId: int("recruit_job_application_id", { unsigned: true })
    .notNull()
    .references(() => recruitJobApplications.id, { onDelete: "cascade", onUpdate: "cascade" }),
  interviewType: mysqlEnum("interview_type", ["in person", "video", "phone"]).default("in person"),
  scheduleDate: datetime("schedule_date"),
  status: mysqlEnum("status", ["rejected", "hired", "pending", "canceled", "completed"]).default(
    "pending",
  ),
  userAcceptStatus: mysqlEnum("user_accept_status", ["accept", "refuse", "waiting"]).default(
    "waiting",
  ),
  meetingId: int("meeting_id"),
  videoType: mysqlEnum("video_type", ["zoom", "other"]).default("other"),
  remindTypeAll: mysqlEnum("remind_type_all", ["day", "hour", "minute"]).notNull(),
  notifyC: boolean("notify_c").default(false),
  remindTimeAll: int("remind_time_all"),
  sendReminderAll: boolean("send_reminder_all").default(false),
  phone: varchar("phone", { length: 255 }),
  otherLink: varchar("other_link", { length: 255 }),
  remarks: varchar("remarks", { length: 255 }),
  recruitInterviewStageId: int("recruit_interview_stage_id", { unsigned: true }).references(
    () => recruitInterviewStages.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  parentId: int("parent_id", { unsigned: true }).references(
    (): AnyMySqlColumn => recruitInterviewSchedules.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_interview_employees — int PK. interview_schedule_id → recruit_interview_schedule_id. */
export const recruitInterviewEmployees = mysqlTable("recruit_interview_employees", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  recruitInterviewScheduleId: int("recruit_interview_schedule_id", { unsigned: true })
    .notNull()
    .references(() => recruitInterviewSchedules.id, { onDelete: "cascade", onUpdate: "cascade" }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  userAcceptStatus: mysqlEnum("user_accept_status", ["accept", "refuse", "waiting"]).default(
    "waiting",
  ),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * recruit_interview_comments — int PK. interview_schedule_id → recruit_interview_schedule_id.
 * +candidate_comment (2023_04_25).
 */
export const recruitInterviewComments = mysqlTable("recruit_interview_comments", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  recruitInterviewScheduleId: int("recruit_interview_schedule_id", { unsigned: true })
    .notNull()
    .references(() => recruitInterviewSchedules.id, { onDelete: "cascade", onUpdate: "cascade" }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  comment: text("comment"),
  candidateComment: varchar("candidate_comment", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_interview_files — int PK. interview_id → recruit_interview_schedule_id. */
export const recruitInterviewFiles = mysqlTable("recruit_interview_files", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  recruitInterviewScheduleId: int("recruit_interview_schedule_id", { unsigned: true })
    .notNull()
    .references(() => recruitInterviewSchedules.id, { onDelete: "cascade", onUpdate: "cascade" }),
  filename: varchar("filename", { length: 255 }).notNull(),
  description: text("description"),
  googleUrl: varchar("google_url", { length: 255 }),
  hashname: varchar("hashname", { length: 255 }),
  size: varchar("size", { length: 255 }),
  dropboxLink: varchar("dropbox_link", { length: 255 }),
  externalLink: varchar("external_link", { length: 255 }),
  externalLinkName: varchar("external_link_name", { length: 255 }),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * recruit_interview_histories — bigint id. interview_schedule_id → recruit_interview_schedule_id,
 * file_id → recruit_interview_file_id.
 */
export const recruitInterviewHistories = mysqlTable("recruit_interview_histories", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  recruitInterviewScheduleId: int("recruit_interview_schedule_id", { unsigned: true }).references(
    () => recruitInterviewSchedules.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  recruitInterviewFileId: int("recruit_interview_file_id", { unsigned: true }).references(
    () => recruitInterviewFiles.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  details: text("details").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * recruit_interview_evaluations — bigint id. +company_id (2022_10_01).
 * Renames: status_id → recruit_recommendation_status_id, interview_schedule_id →
 *   recruit_interview_schedule_id, stage_id → recruit_interview_stage_id,
 *   job_application_id → recruit_job_application_id.
 * submitted_by FK → users (raw onDelete null in PHP → plain FK here).
 */
export const recruitInterviewEvaluations = mysqlTable("recruit_interview_evaluations", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  recruitRecommendationStatusId: int("recruit_recommendation_status_id", { unsigned: true }).references(
    () => recruitRecommendationStatuses.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  recruitInterviewScheduleId: int("recruit_interview_schedule_id", { unsigned: true }).references(
    () => recruitInterviewSchedules.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  recruitInterviewStageId: int("recruit_interview_stage_id", { unsigned: true }).references(
    () => recruitInterviewStages.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  recruitJobApplicationId: int("recruit_job_application_id", { unsigned: true }).references(
    () => recruitJobApplications.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  submittedBy: int("submitted_by", { unsigned: true })
    
    .references(() => users.id, { onUpdate: "cascade" }),
  details: text("details").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** job_interview_stages — int PK. job_id → recruit_job_id (bigint), stage_id → recruit_interview_stage_id. */
export const jobInterviewStages = mysqlTable("job_interview_stages", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  recruitJobId: bigint("recruit_job_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => recruitJobs.id, { onDelete: "cascade", onUpdate: "cascade" }),
  recruitInterviewStageId: int("recruit_interview_stage_id", { unsigned: true })
    .notNull()
    .references(() => recruitInterviewStages.id, { onDelete: "cascade", onUpdate: "cascade" }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ============================== OFFERS ============================== */

/**
 * recruit_job_offer_letter — int PK. +company_id (2022_10_01).
 * Renames: job_app_id → recruit_job_application_id, job_id → recruit_job_id (bigint).
 * employee_id FK → users SET NULL added via raw SQL (2022_07_18). comp_amount → double nullable.
 * +add_structure (int default 0), +description (longText).
 */
export const recruitJobOfferLetter = mysqlTable("recruit_job_offer_letter", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  recruitJobApplicationId: int("recruit_job_application_id", { unsigned: true })
    
    .references(() => recruitJobApplications.id, { onDelete: "cascade", onUpdate: "cascade" }),
  recruitJobId: bigint("recruit_job_id", { mode: "number", unsigned: true })
    
    .references(() => recruitJobs.id, { onDelete: "cascade", onUpdate: "cascade" }),
  employeeId: int("employee_id", { unsigned: true })
    
    .references(() => users.id, { onDelete: "set null", onUpdate: "cascade" }),
  jobExpire: date("job_expire").notNull(),
  expectedJoiningDate: date("expected_joining_date").notNull(),
  compAmount: double("comp_amount"),
  status: varchar("status", { length: 255 }).notNull(),
  payAccording: mysqlEnum("pay_according", ["hour", "day", "week", "month", "year"]).notNull(),
  signRequire: varchar("sign_require", { length: 255 }),
  addStructure: int("add_structure").default(0),
  signImage: varchar("sign_image", { length: 255 }),
  declineReason: varchar("decline_reason", { length: 255 }),
  hash: varchar("hash", { length: 255 }),
  ipAddress: varchar("ip_address", { length: 255 }),
  offerAcceptAt: timestamp("offer_accept_at"),
  description: longtext("description"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_job_offer_files — int PK. job_offer_id → recruit_job_offer_letter_id. */
export const recruitJobOfferFiles = mysqlTable("recruit_job_offer_files", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  recruitJobOfferLetterId: int("recruit_job_offer_letter_id", { unsigned: true })
    .notNull()
    .references(() => recruitJobOfferLetter.id, { onDelete: "cascade", onUpdate: "cascade" }),
  filename: varchar("filename", { length: 255 }).notNull(),
  hashname: varchar("hashname", { length: 255 }),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * offer_letter_histories — bigint id. job_offer_id → recruit_job_offer_letter_id,
 * file_id → recruit_job_offer_file_id.
 */
export const offerLetterHistories = mysqlTable("offer_letter_histories", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  recruitJobOfferLetterId: int("recruit_job_offer_letter_id", { unsigned: true }).references(
    () => recruitJobOfferLetter.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  recruitJobOfferFileId: int("recruit_job_offer_file_id", { unsigned: true }).references(
    () => recruitJobOfferFiles.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  details: text("details").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ============================== JOB HISTORY ============================== */

/**
 * recruit_job_histories — bigint id. Renames: job_id → recruit_job_id (bigint),
 * job_application_id → recruit_job_application_id, offer_id → recruit_job_offer_letter_id,
 * interview_schedule_id → recruit_interview_schedule_id.
 * +recruit_job_application_status_id (2024_05_29). +company_id? — NOT in the company_id sweep list.
 */
export const recruitJobHistories = mysqlTable("recruit_job_histories", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  recruitJobId: bigint("recruit_job_id", { mode: "number", unsigned: true }).references(
    () => recruitJobs.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  recruitJobApplicationId: int("recruit_job_application_id", { unsigned: true }).references(
    () => recruitJobApplications.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  recruitJobOfferLetterId: int("recruit_job_offer_letter_id", { unsigned: true }).references(
    () => recruitJobOfferLetter.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  recruitInterviewScheduleId: int("recruit_interview_schedule_id", { unsigned: true }).references(
    () => recruitInterviewSchedules.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  details: text("details").notNull(),
  recruitJobApplicationStatusId: int("recruit_job_application_status_id", { unsigned: true }).references(
    () => recruitApplicationStatus.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ============================== JOB BOARD / ALERTS ============================== */

/** recruit_jobboard_settings — bigint id. board_column_id → recruit_application_status_id. */
export const recruitJobboardSettings = mysqlTable("recruit_jobboard_settings", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  recruitApplicationStatusId: int("recruit_application_status_id", { unsigned: true })
    .notNull()
    .references(() => recruitApplicationStatus.id, { onDelete: "cascade", onUpdate: "cascade" }),
  collapsed: boolean("collapsed").default(false),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * recruit_job_alerts — int PK. company_id. (created 2023_02_03)
 * recruit_job_category_id is NOT NULL with no default.
 */
export const recruitJobAlerts = mysqlTable("recruit_job_alerts", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  email: varchar("email", { length: 255 }).notNull(),
  recruitWorkExperienceId: bigint("recruit_work_experience_id", { mode: "number", unsigned: true }).references(
    () => recruitWorkExperiences.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  recruitJobTypeId: bigint("recruit_job_type_id", { mode: "number", unsigned: true }).references(
    () => recruitJobTypes.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  recruitJobCategoryId: int("recruit_job_category_id", { unsigned: true })
    .notNull()
    .references(() => recruitJobCategories.id, { onDelete: "cascade", onUpdate: "cascade" }),
  locationId: bigint("location_id", { mode: "number", unsigned: true }).references(
    () => companyAddresses.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  status: varchar("status", { length: 255 }).notNull(),
  hashname: varchar("hashname", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ============================== CUSTOM QUESTIONS ============================== */

/** recruit_custom_questions — int PK. company_id. (created 2023_01_10) */
export const recruitCustomQuestions = mysqlTable("recruit_custom_questions", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  category: mysqlEnum("category", ["job_application", "job_offer"]).default("job_application"),
  question: varchar("question", { length: 255 }).notNull(),
  status: mysqlEnum("status", ["enable", "disable"]).default("disable"),
  type: mysqlEnum("type", [
    "text",
    "number",
    "password",
    "textarea",
    "select",
    "radio",
    "date",
    "checkbox",
    "file",
  ]).default("text"),
  required: mysqlEnum("required", ["yes", "no"]).default("no"),
  values: varchar("values", { length: 5000 }),
  columnPriority: int("column_priority").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_job_questions — int PK. (created 2023_01_10) */
export const recruitJobQuestions = mysqlTable("recruit_job_questions", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  recruitCustomQuestionId: int("recruit_custom_question_id", { unsigned: true })
    .notNull()
    .references(() => recruitCustomQuestions.id, { onDelete: "cascade", onUpdate: "cascade" }),
  recruitJobId: bigint("recruit_job_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => recruitJobs.id, { onDelete: "cascade", onUpdate: "cascade" }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * recruit_job_custom_answers — int PK. (created 2023_01_10)
 * NB: recruit_job_question_id references recruit_custom_questions (per PHP, line 82).
 */
export const recruitJobCustomAnswers = mysqlTable("recruit_job_custom_answers", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  recruitJobOfferLetterId: int("recruit_job_offer_letter_id", { unsigned: true }).references(
    () => recruitJobOfferLetter.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  recruitJobApplicationId: int("recruit_job_application_id", { unsigned: true }).references(
    () => recruitJobApplications.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  recruitJobId: bigint("recruit_job_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => recruitJobs.id, { onDelete: "cascade", onUpdate: "cascade" }),
  recruitJobQuestionId: int("recruit_job_question_id", { unsigned: true })
    .notNull()
    .references(() => recruitCustomQuestions.id, { onDelete: "cascade", onUpdate: "cascade" }),
  answer: varchar("answer", { length: 255 }),
  filename: varchar("filename", { length: 255 }).notNull(),
  hashname: varchar("hashname", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_job_offer_questions — int PK. (created 2023_01_10) */
export const recruitJobOfferQuestions = mysqlTable("recruit_job_offer_questions", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  recruitCustomQuestionId: int("recruit_custom_question_id", { unsigned: true })
    .notNull()
    .references(() => recruitCustomQuestions.id, { onDelete: "cascade", onUpdate: "cascade" }),
  recruitJobOfferLetterId: int("recruit_job_offer_letter_id", { unsigned: true })
    .notNull()
    .references(() => recruitJobOfferLetter.id, { onDelete: "cascade", onUpdate: "cascade" }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ============================== SALARY STRUCTURES ============================== */

/** recruit_salary_structures — int PK. company_id. (created 2023_02_03) */
export const recruitSalaryStructures = mysqlTable("recruit_salary_structures", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  recruitJobApplicationId: int("recruit_job_application_id", { unsigned: true }).references(
    () => recruitJobApplications.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  recruitJobOfferLetterId: int("recruit_job_offer_letter_id", { unsigned: true }).references(
    () => recruitJobOfferLetter.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  salaryJson: text("salary_json"),
  annualSalary: varchar("annual_salary", { length: 255 }),
  basicSalary: varchar("basic_salary", { length: 255 }),
  basicValueType: mysqlEnum("basic_value_type", ["fixed", "ctc_percent"]),
  amount: varchar("amount", { length: 255 }).default("0"),
  fixedAllowance: double("fixed_allowance").default(0),
  date: date("date"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * recruit_selected_salary_components — int PK. company_id. (created 2023_02_03)
 * salary_component_id: no FK in legacy (indexed only); rss_id → recruit_salary_structures.
 */
export const recruitSelectedSalaryComponents = mysqlTable("recruit_selected_salary_components", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  rssId: int("rss_id", { unsigned: true }).references(() => recruitSalaryStructures.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  salaryComponentId: int("salary_component_id", { unsigned: true }), // no FK in legacy (indexed only)
  componentName: varchar("component_name", { length: 255 }),
  componentType: mysqlEnum("component_type", ["earning", "deduction"]),
  componentValue: varchar("component_value", { length: 255 }),
  valueType: mysqlEnum("value_type", ["fixed", "percent", "basic_percent", "variable"]).default(
    "variable",
  ),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ============================== SETTINGS ============================== */

/**
 * recruit_settings — bigint id. company_id (plain unsignedInteger, NO FK in PHP).
 * purchase_code created here then DROPPED (2022_09_01) — not ported.
 * Added cols: form_settings, application_restriction, career_site, offer_letter_reminder,
 *   job_alert_status, google_recaptcha_status.
 */
export const recruitSettings = mysqlTable("recruit_settings", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }), // unsignedInteger, no FK in PHP
  about: longtext("about"),
  type: varchar("type", { length: 255 }).default("bg-image"),
  backgroundImage: varchar("background_image", { length: 255 }),
  backgroundColor: varchar("background_color", { length: 255 }),
  mailSetting: longtext("mail_setting").notNull(),
  formSettings: longtext("form_settings"),
  legalTerm: longtext("legal_term"),
  logo: varchar("logo", { length: 255 }),
  favicon: varchar("favicon", { length: 255 }),
  companyName: varchar("company_name", { length: 255 }),
  companyWebsite: varchar("company_website", { length: 255 }),
  careerSite: mysqlEnum("career_site", ["yes", "no"]).default("yes"),
  offerLetterReminder: int("offer_letter_reminder"),
  jobAlertStatus: mysqlEnum("job_alert_status", ["yes", "no"]).default("no"),
  applicationRestriction: int("application_restriction").default(180),
  googleRecaptchaStatus: mysqlEnum("google_recaptcha_status", ["active", "deactive"]).default(
    "deactive",
  ),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * recruit_global_settings — bigint id. (created 2022_09_01)
 * +notify_update (2023_11_02), +purchased_on (2023_12_19).
 */
export const recruitGlobalSettings = mysqlTable("recruit_global_settings", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  purchaseCode: varchar("purchase_code", { length: 255 }),
  licenseType: varchar("license_type", { length: 20 }),
  supportedUntil: timestamp("supported_until"),
  notifyUpdate: boolean("notify_update").default(true),
  purchasedOn: timestamp("purchased_on"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_email_notification_settings — bigint id. +company_id (2022_10_01). */
export const recruitEmailNotificationSettings = mysqlTable("recruit_email_notification_settings", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  settingName: varchar("setting_name", { length: 255 }).notNull(),
  sendEmail: mysqlEnum("send_email", ["yes", "no"]).default("no"),
  slug: varchar("slug", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** recruit_footer_links — bigint id. +company_id (2022_10_01). */
export const recruitFooterLinks = mysqlTable("recruit_footer_links", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  title: varchar("title", { length: 255 }).notNull(),
  slug: varchar("slug", { length: 255 }).notNull(),
  description: longtext("description"),
  status: varchar("status", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ============================== RECRUITERS ============================== */

/** recruiters — int PK. +company_id (2022_10_01). */
export const recruiters = mysqlTable("recruiters", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  status: mysqlEnum("status", ["enabled", "disabled"]).default("enabled"),
  addedBy: int("added_by", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});
